import { fetchAllPages } from './fetchAllPages';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const catalogApi = {
    addItemToBundle: async (bundleID, sendParams) => {
        const url = `api/inventory/v1/bundles/${bundleID}/item`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    addPropSixtyFiveIngredient: async (extSku, ingredientID) => {
        const url = `api/inventory/v1/propSixtyFive/${extSku}/ingredients`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ ingredientID }),
        });
        return response;
    },
    createBundle: async (sendParams) => {
        const url = `api/inventory/v1/bundles`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    createExternalSkuAisleMapping: async (sendParams) => {
        const url = `api/inventory/v1/aisles/map`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    createSponsoredBrandMapping: async (type, id, brands) => {
        const url = `api/marketing/v1/shopperMarketing/vendorSponsored`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ type, id, brands }),
        });
        return response;
    },
    removeSponsoredBrandMapping: async (id) => {
        const url = `api/marketing/v1/shopperMarketing/vendorSponsored/${id}`;
        const response = await mmFetchWithResponse(url, { method: 'DELETE' });
        return response;
    },
    confirmItemUpload: async (key) => {
        const url = 'api/inventory/v1/itemInformationBulk';
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
        return response;
    },
    editExternalSkuAisleMapping: async (id, sendParams) => {
        const url = `api/inventory/v1/aisles/map/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    deleteExternalSkuAisleMapping: async (id) => {
        const url = `api/inventory/v1/aisles/map/${id}`;
        const response = await mmFetchWithResponse(url, { method: 'DELETE' });
        return response;
    },
    createItemMaster: async (item) => {
        const url = `api/warehouse/v1/itemMaster`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    createTaxonomy: async (sendParams) => {
        const formData = new FormData();
        Object.keys(sendParams).forEach((key) => {
            formData.append(key, sendParams[key]);
        });

        const url = `api/inventory/v1/taxonomy`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    editTaxonomy: async (id, field, value) => {
        const formData = new FormData();
        formData.append(field, value);

        const url = `api/inventory/v1/taxonomy/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    removeItemFromBundle: async (bundleID, externalSku) => {
        const url = `api/inventory/v1/bundles/${bundleID}/item/${externalSku}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });
        return response;
    },
    getBundles: async () => {
        const url = `api/inventory/v1/bundles`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getBrands: async () => {
        const url = `api/warehouse/v1/itemInformation/brands`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getItemInformationSegments: async () => {
        const url = 'api/warehouse/v1/itemInformation/segments';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getItemInformationTiers: async () => {
        const url = 'api/warehouse/v1/itemInformation/tiers';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getRestrictedItemInformation: async (active = false, itemType = '') => {
        let baseUrl = 'api/warehouse/v1/itemInformation?restricted=true';
        if (active) {
            baseUrl += '&active=true';
        }
        if (itemType !== '') {
            baseUrl += `&itemType=${itemType}`;
        }

        // This one passes neither active nor type by default, so it is the largest result set of any caller.
        return fetchAllPages((page, pageSize) => `${baseUrl}&page=${page}&pageSize=${pageSize}`, 'items');
    },
    getItemTempZones: async () => {
        const url = `api/warehouse/v1/itemInformation/tempZones`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getItemInformationTags: async () => {
        const url = 'api/warehouse/v1/itemInformationTags';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPropSixtyFiveIngredientsBySKU: async (extSku) => {
        const url = `api/inventory/v1/propSixtyFive/${extSku}/ingredients`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPropSixtyFiveIngredients: async () => {
        const url = 'api/inventory/v1/propSixtyFiveIngredients';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    deleteTaxonomy: async (taxonomyID) => {
        const url = `api/inventory/v1/taxonomy/${taxonomyID}`;
        const response = await mmFetchWithResponse(url, { method: 'DELETE' });
        return response;
    },
    getTaxonomyByLevel: async (level, type) => {
        const url = `api/inventory/v1/taxonomy?level=${level}&type=${type}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getTaxonomyByParentID: async (parentID, type) => {
        const url = `api/inventory/v1/taxonomy?parentID=${parentID}&type=${type}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getTaxonomyDetailsByID: async (taxonomyID) => {
        const url = `api/inventory/v1/taxonomy/${taxonomyID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    createItemInformationTag: async (name) => {
        const url = 'api/warehouse/v1/itemInformationTags';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ name }),
        });
        return response;
    },
    createItemInformationTagMappings: async (tags, sku) => {
        const url = `api/warehouse/v1/itemInformationTags/map`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ tags, sku }),
        });
        return response;
    },
    removeItemInformationTagMapping: async (tagID, externalSku) => {
        const url = `api/warehouse/v1/itemInformationTag/${tagID}/remove/${externalSku}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });
        return response;
    },
    removePropSixtyFiveIngredient: async (extSku, ingredientID) => {
        const url = `api/inventory/v1/propSixtyFive/${extSku}/ingredients/${ingredientID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });
        return response;
    },
    getItemInformation: async (active = false, itemType = '', { includeRecipeCards = false } = {}) => {
        const params = new URLSearchParams();
        if (active) {
            params.set('active', 'true');
        }
        if (itemType !== '') {
            params.set('type', itemType);
        }
        if (!includeRecipeCards) {
            params.set('excludeRecipeCards', 'true');
        }

        return fetchAllPages((page, pageSize) => {
            const pageParams = new URLSearchParams(params);
            pageParams.set('page', String(page));
            pageParams.set('pageSize', String(pageSize));
            return `api/warehouse/v1/itemInformation?${pageParams.toString()}`;
        }, 'items');
    },
    updateBundle: async (data) => {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
        });

        const url = `api/inventory/v1/bundles/${data.id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            processData: false,
            headers: {},
            body: formData,
        });
        return response;
    },
    updateDefaultPriceByExternalSkuFacilityID: async ({ defaultPrice, externalSku, facilityID }) => {
        const url = `api/warehouse/v1/itemInformation/priceOverride`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ externalSku, facilityID, defaultPrice }),
        });
        return response;
    },
    updateItemInformation: async (formData) => {
        const url = 'api/warehouse/v1/itemInformation';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            processData: false,
            headers: {},
            body: formData,
        });
        return response;
    },
    updatePropSixtyFive: async (extSku, item) => {
        const url = `api/inventory/v1/propSixtyFive/${extSku}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updateItemMaster: async (id, item) => {
        const url = `api/warehouse/v1/itemMaster/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    upsertCustomerGuaranteeDaysByCompanySource: async (externalSku, customerGuaranteeDaysByCompanySource) => {
        const url = `api/catalog/v1/external-skus/${externalSku}/customer-guarantee-days-by-company-source`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ customerGuaranteeDaysByCompanySource }),
        });
        return response;
    },
    uploadAisleTaxonomy: async (data) => {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
        });

        const url = `api/warehouse/v1/itemInformation/aisles`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    uploadItemInformation: async (data) => {
        const formData = new FormData();
        formData.append('itemFile', data.file);

        const url = 'api/inventory/v1/itemInformationBulk';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    bulkUpdateItemInfoTaxonomy: async (sendParams) => {
        const url = `api/inventory/v1/itemInformationBulk/taxonomy`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
    },
    setAisleTaxonomy: async (key) => {
        const url = `api/warehouse/v1/itemInformation/aisles`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
        return response;
    },
    createItemMasterReplenishmentFacility: async (facilitiesId, itemMasterId) => {
        const url = `api/warehouse/v1/itemMasterReplenishmentFacility`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ facilitiesId, itemMasterId }),
        });
        return response;
    },
    removeItemMasterReplenishmentFacility: async (id) => {
        const url = `api/warehouse/v1/itemMasterReplenishmentFacility/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });
        return response;
    },
    getItemInformationPhotoInfo: async (ids) => {
        const url = `api/warehouse/v1/itemInformation/photoInfo?ids=${ids.join(',')}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getItemInformationNutritionInfo: async (externalSku) => {
        const url = `api/warehouse/v1/itemInformation/nutritionInfo/${externalSku}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    parseItemInformationNutritionLabel: async (externalSku) => {
        const url = `api/warehouse/v1/itemInformation/nutritionInfo/${externalSku}/parse`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    updateItemInformationNutritionInfo: async (externalSku, nutritionInfo, isHumanValidated) => {
        const url = `api/warehouse/v1/itemInformation/nutritionInfo/${externalSku}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ nutritionInfo, isHumanValidated }),
        });
        return response;
    },
    bulkItemMasterUpload: (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const url = 'api/warehouse/v1/itemMaster/bulk/upload';
        return mmFetchWithResponse(url, {
            body: formData,
            headers: {},
            method: 'POST',
        });
    },
    bulkItemMasterFetch: (key, _params, signal) => {
        const url = `api/warehouse/v1/itemMaster/bulk/fetch?key=${encodeURIComponent(key)}`;
        return mmFetchWithResponse(url, { signal });
    },
    bulkItemMasterEdit: (key, _params, rowsToUpdate, rowsToDelete) => {
        const url = 'api/warehouse/v1/itemMaster/bulk/edit';
        return mmFetchWithResponse(url, {
            body: JSON.stringify({ key, rowsToUpdate, rowsToDelete }),
            method: 'PUT',
        });
    },
    bulkItemMasterPersist: (key) => {
        const url = 'api/warehouse/v1/itemMaster/bulk/persist';
        return mmFetchWithResponse(url, {
            body: JSON.stringify({ key }),
            method: 'PUT',
        });
    },
};

export { catalogApi };
